import type {
  AdapterExecutionResult,
  EvidencePatch,
  IntegrityEvidence,
  TrustEvidence,
} from './types.js';
import type {
  Action,
  Actor,
  Assertion,
  Claim,
  Credential,
  Diagnostic,
  EvidenceEdge,
  Signal,
  Tool,
} from '@upl/evidence-schema';

export interface MergedEvidence {
  signals: Signal[];
  credentials: Credential[];
  claims: Claim[];
  assertions: Assertion[];
  actions: Action[];
  actors: Actor[];
  tools: Tool[];
  trustEvidence: TrustEvidence[];
  integrityEvidence: IntegrityEvidence[];
  edges: EvidenceEdge[];
  warnings: Diagnostic[];
  limitations: Diagnostic[];
  acceptedAdapterIds: string[];
  rejected: Array<{ adapterId: string; evidenceId: string }>;
}

type Identified = { id: string };

export class EvidenceMerger {
  merge(results: readonly AdapterExecutionResult[]): MergedEvidence {
    const output: MergedEvidence = {
      signals: [], credentials: [], claims: [], assertions: [], actions: [], actors: [], tools: [],
      trustEvidence: [], integrityEvidence: [], edges: [], warnings: [], limitations: [],
      acceptedAdapterIds: [], rejected: [],
    };
    const seen = new Map<string, string>();
    for (const result of [...results].sort((left, right) => left.adapter.id.localeCompare(right.adapter.id))) {
      mergeCollection(output.warnings, result.warnings, seen);
      mergeCollection(output.limitations, result.limitations, seen);
      if (result.execution.state !== 'COMPLETED') continue;
      const patch = result.evidence;
      const identified = identifiedValues(patch);
      const prospective = new Map(seen);
      let conflict: string | undefined;
      for (const value of identified) {
        const canonical = stableJson(value);
        const known = prospective.get(value.id);
        if (known !== undefined && known !== canonical) {
          conflict = value.id;
          break;
        }
        prospective.set(value.id, canonical);
      }
      if (conflict) {
        output.rejected.push({ adapterId: result.adapter.id, evidenceId: conflict });
        continue;
      }
      output.acceptedAdapterIds.push(result.adapter.id);
      mergeCollection(output.signals, patch.signals, seen);
      mergeCollection(output.credentials, patch.credentials, seen);
      mergeCollection(output.claims, patch.claims, seen);
      mergeCollection(output.assertions, patch.assertions, seen);
      mergeCollection(output.actions, patch.actions, seen);
      mergeCollection(output.actors, patch.actors, seen);
      mergeCollection(output.tools, patch.tools, seen);
      mergeCollection(output.trustEvidence, patch.trustEvidence, seen);
      mergeCollection(output.integrityEvidence, patch.integrityEvidence, seen);
      mergeCollection(output.edges, patch.edges, seen);
      mergeCollection(output.warnings, patch.warnings, seen);
      mergeCollection(output.limitations, patch.limitations, seen);
    }
    for (const collection of allCollections(output)) collection.sort((left, right) => left.id.localeCompare(right.id));
    output.acceptedAdapterIds.sort();
    output.rejected.sort((left, right) => left.adapterId.localeCompare(right.adapterId));
    return output;
  }
}

function identifiedValues(patch: EvidencePatch): Identified[] {
  return [
    ...patch.signals, ...patch.credentials, ...patch.claims, ...patch.assertions, ...patch.actions,
    ...patch.actors, ...patch.tools, ...patch.trustEvidence, ...patch.integrityEvidence, ...patch.edges,
    ...patch.warnings, ...patch.limitations,
  ];
}

function allCollections(output: MergedEvidence): Identified[][] {
  return [
    output.signals, output.credentials, output.claims, output.assertions, output.actions,
    output.actors, output.tools, output.trustEvidence, output.integrityEvidence, output.edges,
    output.warnings, output.limitations,
  ];
}

function mergeCollection<T extends Identified>(target: T[], values: readonly T[], seen: Map<string, string>): void {
  for (const value of values) {
    const canonical = stableJson(value);
    if (seen.get(value.id) === canonical) continue;
    seen.set(value.id, canonical);
    target.push(value);
  }
}

export function stableJson(value: unknown): string {
  return JSON.stringify(sortValue(value));
}

function sortValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortValue);
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).sort(([left], [right]) => left.localeCompare(right)).map(([key, entry]) => [key, sortValue(entry)]));
  }
  return value;
}
